import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, RefreshCw, Save, Link2, Unlink, AlertTriangle, Lock } from "lucide-react";

import useAuthStore from "@/store/authStore";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import ZohoCustomerSearch from "@/components/ZohoCustomerSearch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SENTINEL_UNMAPPED = "__UNMAPPED__";

function VendorBranchMappingTab() {
  const { token } = useAuthStore();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [vendorBranches, setVendorBranches] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [drafts, setDrafts] = useState({}); // branch_id -> vendor_id selected
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [bRes, vRes] = await Promise.all([
        axios.get(`${API}/branches/vendor-mappings`, { headers }),
        axios.get(`${API}/vendors`, { headers }),
      ]);
      const mappings = Array.isArray(bRes.data?.mappings) ? bRes.data.mappings : [];
      setVendorBranches(mappings);
      const vendorList = Array.isArray(vRes.data) ? vRes.data : [];
      setVendors(vendorList.filter((v) => v.vendor_id && v.name));
      // Reset drafts on every refresh — pre-fill mapped ones with their current vendor.
      const seed = {};
      for (const m of mappings) {
        seed[m.branch_id] = m.vendor_id || SENTINEL_UNMAPPED;
      }
      setDrafts(seed);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load vendor-branch mappings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveRow = async (row, choice) => {
    // Auto-save on dropdown change: `choice` is passed in directly so
    // we don't depend on the `drafts` state having flushed yet. Save
    // is silently skipped for the sentinel placeholder.
    if (!choice || choice === SENTINEL_UNMAPPED) return;
    setSavingId(row.branch_id);
    try {
      await axios.put(
        `${API}/branches/${row.branch_id}/vendor-mapping`,
        { vendor_id: choice },
        { headers }
      );
      toast.success(`Mapped ${row.name} to vendor`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to map vendor");
    } finally {
      setSavingId(null);
    }
  };

  const unmapRow = async (row) => {
    if (row.has_fg_inventory) {
      toast.error("FG inventory exists at this branch — cannot unmap.");
      return;
    }
    const confirmed = window.confirm(
      `Unmap vendor "${row.vendor_name}" from "${row.name}"?\n\nAfter unmapping you can assign a different vendor.`
    );
    if (!confirmed) return;
    setSavingId(row.branch_id);
    try {
      await axios.delete(`${API}/branches/${row.branch_id}/vendor-mapping`, { headers });
      toast.success(`Unmapped vendor from ${row.name}`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to unmap vendor");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="vendor-branch-mapping-tab">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">
            Map every <span className="font-medium">VENDOR_SITE</span> branch to the vendor that owns/operates it.
            Once mapped, FG inward, valuation, and Zoho payloads from this branch automatically resolve the vendor.
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Set-once: to reassign, click <span className="font-medium">Unmap</span> first. Unmap is blocked while
            FG inventory exists at the branch.
          </p>
        </div>
        <Button
          onClick={refresh}
          variant="outline"
          size="sm"
          disabled={loading}
          data-testid="vendor-branch-mapping-refresh"
        >
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
          Refresh
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Branch</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Mapped Vendor</TableHead>
                <TableHead>FG Inventory</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {vendorBranches.map((row) => {
                const saving = savingId === row.branch_id;
                const draft = drafts[row.branch_id] ?? SENTINEL_UNMAPPED;
                return (
                  <TableRow key={row.branch_id} data-testid={`vendor-mapping-row-${row.branch_id}`}>
                    <TableCell className="font-medium">{row.name}</TableCell>
                    <TableCell className="text-muted-foreground">{row.code || row.branch_id}</TableCell>
                    <TableCell>
                      {row.is_mapped ? (
                        <span data-testid={`vendor-mapping-current-${row.branch_id}`}>
                          {row.vendor_name || row.vendor_id}
                          <span className="ml-2 text-xs text-muted-foreground">({row.vendor_id})</span>
                        </span>
                      ) : (
                        <Select
                          value={draft}
                          disabled={saving}
                          onValueChange={(v) => {
                            // Optimistic local update + auto-save in the
                            // same tick. No "Save" click required — picking
                            // the vendor IS the commit. Placeholder value
                            // is short-circuited inside `saveRow`.
                            setDrafts((d) => ({ ...d, [row.branch_id]: v }));
                            saveRow(row, v);
                          }}
                        >
                          <SelectTrigger
                            className="w-[280px]"
                            data-testid={`vendor-mapping-select-${row.branch_id}`}
                          >
                            <SelectValue placeholder="-- Select vendor --" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={SENTINEL_UNMAPPED}>
                              <span className="text-muted-foreground">— Select vendor —</span>
                            </SelectItem>
                            {vendors.map((v) => (
                              <SelectItem key={v.vendor_id} value={v.vendor_id}>
                                {v.name}
                                <span className="ml-2 text-xs text-muted-foreground">({v.vendor_id})</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.has_fg_inventory ? (
                        <Badge variant="destructive" className="gap-1">
                          <Lock className="h-3 w-3" /> Has Stock
                        </Badge>
                      ) : (
                        <Badge variant="outline">Empty</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.is_mapped ? (
                        <Badge variant="default" className="gap-1">
                          <Link2 className="h-3 w-3" /> Mapped
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="gap-1">
                          <Unlink className="h-3 w-3" /> Unmapped
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {row.is_mapped ? (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={saving || row.has_fg_inventory}
                            onClick={() => unmapRow(row)}
                            title={
                              row.has_fg_inventory
                                ? "Cannot unmap — FG inventory exists at this branch"
                                : "Unmap to reassign"
                            }
                            data-testid={`vendor-mapping-unmap-${row.branch_id}`}
                          >
                            {saving ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                              <Unlink className="mr-2 h-4 w-4" />
                            )}
                            Unmap
                          </Button>
                        ) : saving ? (
                          // No Save button — vendor pick auto-commits via
                          // the Select's `onValueChange`. Inline spinner
                          // signals the in-flight save.
                          <span
                            className="inline-flex items-center text-xs text-muted-foreground"
                            data-testid={`vendor-mapping-saving-${row.branch_id}`}
                          >
                            <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                            Saving…
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            Pick a vendor to map
                          </span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!vendorBranches.length && !loading && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    No VENDOR_SITE branches found. Create one under Tech Ops with branch_type = VENDOR_SITE first.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function ZohoBranchMappingTab() {
  const { token } = useAuthStore();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [zohoBranches, setZohoBranches] = useState([]);
  const [zohoConfigured, setZohoConfigured] = useState(true);
  const [zohoAddonDisabled, setZohoAddonDisabled] = useState(false);
  const [zohoMessage, setZohoMessage] = useState("");
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  // Drafts: branch_id (Playfield) → zoho_branch_id selected in dropdown
  const [drafts, setDrafts] = useState({});

  const refresh = async () => {
    setLoading(true);
    try {
      const [zRes, mRes] = await Promise.all([
        axios.get(`${API}/zoho/branches`, { headers }),
        axios.get(`${API}/zoho/branch-mappings`, { headers }),
      ]);
      const zd = zRes.data || {};
      setZohoConfigured(!!zd.configured);
      setZohoAddonDisabled(!!zd.addon_disabled);
      setZohoMessage(zd.message || "");
      setZohoBranches(Array.isArray(zd.branches) ? zd.branches : []);

      const md = mRes.data || {};
      const rows = Array.isArray(md.mappings) ? md.mappings : [];
      setMappings(rows);
      // Seed drafts from current mapped values
      const seed = {};
      for (const r of rows) {
        seed[r.branch_id] = r.zoho_branch_id || SENTINEL_UNMAPPED;
      }
      setDrafts(seed);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Zoho branch data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isDirty = (row) => {
    const draft = drafts[row.branch_id];
    const current = row.zoho_branch_id || SENTINEL_UNMAPPED;
    return draft !== current;
  };

  const saveRow = async (row) => {
    const choice = drafts[row.branch_id];
    setSavingId(row.branch_id);
    try {
      let body;
      if (!choice || choice === SENTINEL_UNMAPPED) {
        body = { zoho_branch_id: null };
      } else {
        const zb = zohoBranches.find((b) => b.branch_id === choice);
        body = {
          zoho_branch_id: choice,
          zoho_branch_name: zb?.branch_name || "",
        };
      }
      await axios.put(`${API}/zoho/branch-mappings/${row.branch_id}`, body, { headers });
      toast.success(`Mapping saved for ${row.name}`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save mapping");
    } finally {
      setSavingId(null);
    }
  };

  const clearRow = async (row) => {
    setSavingId(row.branch_id);
    try {
      await axios.put(
        `${API}/zoho/branch-mappings/${row.branch_id}`,
        { zoho_branch_id: null },
        { headers }
      );
      toast.success(`Cleared mapping for ${row.name}`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to clear mapping");
    } finally {
      setSavingId(null);
    }
  };

  // Build active list of Zoho branches (drop inactive). Always keep the
  // currently-mapped one even if its status is inactive, so the current
  // selection can still be displayed.
  const activeZohoBranches = useMemo(() => {
    const active = zohoBranches.filter((b) => (b.branch_status || "").toLowerCase() === "active");
    const activeIds = new Set(active.map((b) => b.branch_id));
    const extras = mappings
      .map((m) => zohoBranches.find((b) => b.branch_id === m.zoho_branch_id))
      .filter((b) => b && !activeIds.has(b.branch_id));
    return [...active, ...extras];
  }, [zohoBranches, mappings]);

  if (!zohoConfigured) {
    return (
      <Alert variant="destructive" data-testid="settings-zoho-not-configured">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Zoho Books not configured</AlertTitle>
        <AlertDescription>
          Connect Zoho Books from your environment configuration before mapping branches.
        </AlertDescription>
      </Alert>
    );
  }

  if (zohoAddonDisabled) {
    return (
      <Alert data-testid="settings-zoho-addon-disabled">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Multi-Branch add-on is not enabled</AlertTitle>
        <AlertDescription>{zohoMessage || "Enable Multi-Branch in Zoho Books to use this feature."}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4" data-testid="zoho-branch-mapping-tab">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">
            Map every Playfield branch to its corresponding Zoho Books branch. The selected mapping is sent as
            <code className="mx-1 rounded bg-muted px-1 py-0.5">branch_id</code>
            on every Bill, Invoice, and Delivery Challan created from that branch.
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Unmapped branches default to the Zoho org's primary branch.
          </p>
        </div>
        <Button
          onClick={refresh}
          variant="outline"
          size="sm"
          disabled={loading}
          data-testid="zoho-branch-mapping-refresh"
        >
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
          Refresh
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Playfield Branch</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Mapped Zoho Branch</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {mappings.map((row) => {
                const draft = drafts[row.branch_id] ?? SENTINEL_UNMAPPED;
                const dirty = isDirty(row);
                const saving = savingId === row.branch_id;
                return (
                  <TableRow key={row.branch_id} data-testid={`zoho-mapping-row-${row.branch_id}`}>
                    <TableCell className="font-medium">{row.name}</TableCell>
                    <TableCell className="text-muted-foreground">{row.code}</TableCell>
                    <TableCell>
                      <Select
                        value={draft}
                        onValueChange={(v) =>
                          setDrafts((d) => ({ ...d, [row.branch_id]: v }))
                        }
                      >
                        <SelectTrigger
                          className="w-[280px]"
                          data-testid={`zoho-mapping-select-${row.branch_id}`}
                        >
                          <SelectValue placeholder="-- Select Zoho branch --" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={SENTINEL_UNMAPPED}>
                            <span className="text-muted-foreground">— Not mapped —</span>
                          </SelectItem>
                          {activeZohoBranches.map((zb) => (
                            <SelectItem key={zb.branch_id} value={zb.branch_id}>
                              {zb.branch_name}
                              {zb.is_primary_branch ? " (primary)" : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      {row.is_mapped ? (
                        <Badge variant="default" className="gap-1">
                          <Link2 className="h-3 w-3" /> Mapped
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="gap-1">
                          <Unlink className="h-3 w-3" /> Unmapped
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {row.is_mapped && (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={saving}
                            onClick={() => clearRow(row)}
                            data-testid={`zoho-mapping-clear-${row.branch_id}`}
                          >
                            Clear
                          </Button>
                        )}
                        <Button
                          size="sm"
                          disabled={!dirty || saving}
                          onClick={() => saveRow(row)}
                          data-testid={`zoho-mapping-save-${row.branch_id}`}
                        >
                          {saving ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <Save className="mr-2 h-4 w-4" />
                          )}
                          Save
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!mappings.length && !loading && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                    No branches found in the database. Add branches under Tech Ops first.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function ZohoCustomerMappingTab() {
  // Jul 2026 — new mapping tab. Populates the `branches.zoho_customer_id`
  // FK that IBT Ship uses to auto-create the Zoho Delivery Challan.
  // Excludes VENDOR_SITE branches server-side.
  const { token } = useAuthStore();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [editingBranchId, setEditingBranchId] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/branches/zoho-customer-mappings`, { headers });
      setRows(Array.isArray(res.data?.mappings) ? res.data.mappings : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load branch → customer mappings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const commit = async (row, next) => {
    setSavingId(row.branch_id);
    try {
      await axios.put(
        `${API}/branches/${row.branch_id}/zoho-customer-mapping`,
        next
          ? { zoho_customer_id: next.customer_id, zoho_customer_name: next.customer_name }
          : { zoho_customer_id: null },
        { headers },
      );
      toast.success(next ? `Mapped ${row.name}` : `Cleared mapping for ${row.name}`);
      setEditingBranchId(null);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save mapping");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="zoho-customer-mapping-tab">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">
            Map every Bidso branch to its counterpart Zoho <span className="font-medium">customer</span> entity
            (the customer-role contact that represents the branch in Books).
            Once mapped, an IBT ship into this branch auto-creates the Zoho Delivery Challan
            with the correct customer — no manual search at ship time.
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            VENDOR_SITE branches are excluded — their Zoho counterpart is a vendor, not a customer.
          </p>
        </div>
        <Button
          onClick={refresh}
          variant="outline"
          size="sm"
          disabled={loading}
          data-testid="zoho-customer-mapping-refresh"
        >
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
          Refresh
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Branch</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Mapped Zoho Customer</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const saving = savingId === row.branch_id;
                const editing = editingBranchId === row.branch_id;
                return (
                  <TableRow key={row.branch_id} data-testid={`zoho-cust-row-${row.branch_id}`}>
                    <TableCell className="font-medium">{row.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-mono text-[10px]">
                        {row.branch_type || "—"}
                      </Badge>
                    </TableCell>
                    <TableCell className="min-w-[320px]">
                      {editing ? (
                        <ZohoCustomerSearch
                          value={null}
                          onChange={(next) => next && commit(row, next)}
                          testIdPrefix={`zoho-cust-search-${row.branch_id}`}
                          disabled={saving}
                        />
                      ) : row.is_mapped ? (
                        <div className="text-sm">
                          <div className="font-medium">
                            {row.zoho_customer_name || "(name not cached)"}
                          </div>
                          <div className="text-[11px] text-zinc-500 font-mono">
                            {row.zoho_customer_id}
                          </div>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">— No mapping —</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.is_mapped ? (
                        <Badge variant="default" className="gap-1">
                          <Link2 className="h-3 w-3" /> Mapped
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="gap-1">
                          <Unlink className="h-3 w-3" /> Unmapped
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {saving && <Loader2 className="h-4 w-4 animate-spin text-zinc-500" />}
                        {row.is_mapped && !editing && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={saving}
                              onClick={() => setEditingBranchId(row.branch_id)}
                              data-testid={`zoho-cust-change-${row.branch_id}`}
                            >
                              Change
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={saving}
                              onClick={() => commit(row, null)}
                              data-testid={`zoho-cust-clear-${row.branch_id}`}
                            >
                              Clear
                            </Button>
                          </>
                        )}
                        {!row.is_mapped && !editing && (
                          <Button
                            size="sm"
                            disabled={saving}
                            onClick={() => setEditingBranchId(row.branch_id)}
                            data-testid={`zoho-cust-map-${row.branch_id}`}
                          >
                            Map Customer
                          </Button>
                        )}
                        {editing && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingBranchId(null)}
                            data-testid={`zoho-cust-cancel-${row.branch_id}`}
                          >
                            Cancel
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!rows.length && !loading && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                    No mappable branches found. Add non-VENDOR_SITE branches under Tech Ops first.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

export default function Settings() {
  return (
    <div className="container mx-auto p-6 space-y-6" data-testid="branch-mapping-page">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">Branch Mapping</h1>
        <p className="text-sm text-muted-foreground">
          Map Playfield branches to their counterparts in connected accounting integrations.
        </p>
      </div>

      <Tabs defaultValue="zoho-branches" className="space-y-4">
        <TabsList>
          <TabsTrigger value="zoho-branches" data-testid="branch-mapping-tab-zoho">
            Zoho Books
          </TabsTrigger>
          <TabsTrigger value="zoho-customers" data-testid="branch-mapping-tab-zoho-customer">
            Zoho Customer
          </TabsTrigger>
          <TabsTrigger value="vendor-branches" data-testid="branch-mapping-tab-vendor">
            Vendor Mapping
          </TabsTrigger>
        </TabsList>
        <TabsContent value="zoho-branches">
          <Card>
            <CardHeader>
              <CardTitle>Zoho Branch Mapping</CardTitle>
            </CardHeader>
            <CardContent>
              <ZohoBranchMappingTab />
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="zoho-customers">
          <Card>
            <CardHeader>
              <CardTitle>Branch → Zoho Customer</CardTitle>
            </CardHeader>
            <CardContent>
              <ZohoCustomerMappingTab />
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="vendor-branches">
          <Card>
            <CardHeader>
              <CardTitle>Vendor-Site Branch ⇄ Vendor Mapping</CardTitle>
            </CardHeader>
            <CardContent>
              <VendorBranchMappingTab />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
